/* eslint-disable no-console */
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { appConfig } from 'constants/appConfig';
import { useAppSelector } from 'hooks/store';
import { UserDataSelector } from 'store/user';

import { SignalREvents } from 'constants/signalR';
import { SignalRContext } from 'contexts/signalR';
import { enqueueSnackbar } from 'notistack';

/**
 * @interface IProps
 * @description Child component parameters that will be wrapped in this component
 */
interface IProps {
  children: ReactNode;
}

/**
 * @function SignalRProvider
 * @description A functional component that provides SignalR connection functionality
 * @param {ReactNode} children - The child components to be rendered within this component
 */
const SignalRProvider: React.FC<IProps> = ({ children }) => {
  const [methods, setMethods] = useState<string[]>([]);
  const [established, setEstablished] = useState<boolean>(false);

  const user = useAppSelector(UserDataSelector);

  /**
   * @description Establish SignalR connection
   */
  const signalRConnection = useMemo(
    () =>
      (!!appConfig.signalRUrl &&
        new HubConnectionBuilder()
          .withUrl(appConfig.signalRUrl)
          .withAutomaticReconnect()
          .build()) ||
      undefined,
    [],
  );

  signalRConnection?.onreconnected(() => {
    if (user?.tenantId && !established && signalRConnection?.state === HubConnectionState.Connected) {
      signalRConnection.send(SignalREvents.TenantSubscription, user.tenantId);
    }
  });

  /**
   * @description Use effect to start and stop SignalR connection
   */
  useEffect(() => {
    if (signalRConnection) {
      signalRConnection.start();
    }

    return () => {
      if (signalRConnection) {
        signalRConnection.stop();
      }
    };
  }, [signalRConnection]);

  /**
   * @description Use effect to handle tenant subscription
   */
  useEffect(() => {
    if (signalRConnection && user.tenantId && !established) {
      const interval = setInterval(() => {
        console.log(signalRConnection.state);
        // If the SignalR connection state is "Connected", send TenantSubscription
        if (signalRConnection.state === HubConnectionState.Connected) {
          clearInterval(interval);
          signalRConnection.send(SignalREvents.TenantSubscription, user?.tenantId);
        }
      }, 1000);

      setTimeout(() => {
        if (signalRConnection.state !== HubConnectionState.Connected) {
          console.log('Error while connection to resource. Please reload this page');
          enqueueSnackbar('Connection in progress.', { variant: 'warning' });
        }

        if (!user?.tenantId) {
          console.log('tenant id was not provided properly', user?.tenantId);
          enqueueSnackbar('Connection in progress.', { variant: 'warning' });
        }
      }, 4000);

      setTimeout(() => {
        if (signalRConnection.state !== HubConnectionState.Connected) {
          enqueueSnackbar('Connection in progress.', {
            variant: 'warning',
          });
        }
      }, 5000);

      const connectionCheck = setInterval(() => {
        if (signalRConnection.state === HubConnectionState.Connected && user.tenantId) {
          enqueueSnackbar('Connection Established', { variant: 'success' });
          setEstablished(true);
          clearInterval(connectionCheck);
        }
      }, 1000);
    }
  }, [signalRConnection, user, established]);

  return (
    <SignalRContext.Provider
      value={{
        connection: signalRConnection,
        methods,
        setMethods: arg => {
          setMethods([...methods, arg]);
        },
      }}
    >
      {children}
    </SignalRContext.Provider>
  );
};

export default SignalRProvider;
